import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import MonthPicker from '@/components/MonthPicker';
import { ApiError, api } from '@/lib/api';

/**
 * Создание ручного начисления по основному договору (school/kindergarten).
 *
 * Используется во вкладке «Начисления» в карточке ученика. Бизнес-кейс:
 * бухгалтер хочет проставить начисление за конкретный месяц с
 * собственной суммой (например, со скидкой за «семейные обстоятельства»)
 * и комментарием. UI следит, чтобы:
 *   • месяц попадал в период действия договора;
 *   • за этот месяц по договору ещё нет другого начисления (UNIQUE-ключ
 *     не даст вставить дубль; в этом случае предлагаем удалить старое).
 *
 * Передаваемые props:
 *   • agreement — { id, type, number, dateStart, dateEnd, dateTermination, tariffPrice, ... }
 *   • usedMonths — массив строк YYYY-MM, для которых уже есть main_charges
 *   • onClose, onCreated
 */
const TYPE_LABEL = {
  school:       'Школа',
  kindergarten: 'Детский сад',
};

function pad2(n) { return String(n).padStart(2, '0'); }
function fmtAmount(v) {
  if (v === null || v === undefined || v === '') return '';
  return Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' ₽';
}
function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
function ruMonth(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym || '';
  const months = ['январь','февраль','март','апрель','май','июнь',
                  'июль','август','сентябрь','октябрь','ноябрь','декабрь'];
  const [y, m] = ym.split('-');
  return `${months[(parseInt(m, 10) - 1) || 0]} ${y}`;
}

/**
 * Считает «эффективную правую границу» договора — date_termination имеет
 * приоритет, и от неё мы отнимаем 1 день (последний учётный день — за день
 * до расторжения, как в mainMonthly). Иначе — date_end.
 */
function effectiveEnd(agr) {
  if (agr.dateTermination) {
    const d = new Date(agr.dateTermination);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }
  return agr.dateEnd || '9999-12-31';
}

/**
 * Дефолтный месяц: текущий, если он попадает в [start, end] и ещё не
 * использован; иначе первый незанятый месяц периода договора.
 */
function pickInitialMonth(agr, used) {
  const start = (agr.dateStart || '').slice(0, 7);
  const end   = effectiveEnd(agr).slice(0, 7);
  const usedSet = new Set(used || []);
  const today = new Date();
  const cur = today.getFullYear() + '-' + pad2(today.getMonth() + 1);
  if (cur >= start && cur <= end && !usedSet.has(cur)) return cur;
  // Поиск первого незанятого месяца от начала договора.
  let [y, m] = start.split('-').map(Number);
  while (true) {
    const ym = y + '-' + pad2(m);
    if (ym > end) break;
    if (!usedSet.has(ym)) return ym;
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return start; // запасной вариант — даже если занят, подсветим ошибку
}

export default function MainChargeCreateModal({ agreement, usedMonths, onClose, onCreated }) {
  const [month, setMonth]     = useState(() => pickInitialMonth(agreement, usedMonths));
  const [amount, setAmount]   = useState(() =>
    agreement.tariffPrice != null ? String(Math.round(agreement.tariffPrice)) : ''
  );
  const [comment, setComment] = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);

  const startYM = useMemo(() => (agreement.dateStart || '').slice(0, 7), [agreement.dateStart]);
  const endYM   = useMemo(() => effectiveEnd(agreement).slice(0, 7), [agreement]);
  const usedSet = useMemo(() => new Set(usedMonths || []), [usedMonths]);

  // Inline-валидация: какая «причина», если месяц нельзя выбрать?
  // null — всё ок.
  const monthIssue = useMemo(() => {
    if (!month) return 'Выберите месяц';
    if (month < startYM) return `До начала договора (${ruMonth(startYM)})`;
    if (month > endYM)   return `После конца договора (${ruMonth(endYM)})`;
    if (usedSet.has(month)) return 'За этот месяц начисление уже есть';
    return null;
  }, [month, startYM, endYM, usedSet]);

  const amountIssue = useMemo(() => {
    if (amount === '' || amount === null) return 'Укажите сумму';
    const n = Number(amount);
    if (!isFinite(n)) return 'Сумма должна быть числом';
    if (n < 0) return 'Сумма не может быть отрицательной';
    return null;
  }, [amount]);

  // ESC закрытие.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (monthIssue || amountIssue) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post('/api/charges/main-monthly/manual', {
        agreement_id: agreement.id,
        month,
        amount: Number(amount),
        comment: comment.trim() || null,
      });
      onCreated && onCreated(res);
    } catch (e2) {
      setError(e2 instanceof ApiError ? e2.message : 'Не удалось создать начисление');
    } finally {
      setBusy(false);
    }
  };

  const submitDisabled = busy || !!monthIssue || !!amountIssue;
  const typeLabel = TYPE_LABEL[agreement.type] || agreement.type;

  return (
    <div
      className="sd-overlay"
      onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !busy) onClose(); }}
    >
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>Создать начисление</h2>
          <button className="sd-close" onClick={onClose} disabled={busy} aria-label="Закрыть">
            <Icon name="x" size={14} />
          </button>
        </div>

        <form className="sd-edit__form" onSubmit={submit} noValidate>
          <div className="sd-edit__body">
            {/* Контекст договора */}
            <div className="sd-edit__field sd-edit__field--full">
              <div style={{
                fontSize: 12, color: 'var(--fg-muted)', display: 'flex',
                gap: 8, flexWrap: 'wrap', alignItems: 'center',
              }}>
                <span className="agr-row__type-pill">{typeLabel}</span>
                <span>№ {agreement.number || '—'}</span>
                <span>·</span>
                <span>{fmtDateRu(agreement.dateStart)} — {fmtDateRu(agreement.dateTermination || agreement.dateEnd)}</span>
                {agreement.tariffPrice != null && (
                  <>
                    <span>·</span>
                    <span>тариф {fmtAmount(agreement.tariffPrice)}/мес</span>
                  </>
                )}
              </div>
            </div>

            {/* Месяц */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Месяц *</label>
              <MonthPicker value={month || ''} onChange={(v) => setMonth(v)} />
              {monthIssue && (
                <div style={{ color: 'var(--red-fg)', fontSize: 11.5, marginTop: 4 }}>
                  {monthIssue}
                </div>
              )}
              {!monthIssue && (
                <div style={{ color: 'var(--fg-faint)', fontSize: 11.5, marginTop: 4 }}>
                  Допустимый период: {ruMonth(startYM)} — {ruMonth(endYM)}
                </div>
              )}
            </div>

            {/* Сумма */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Сумма, ₽ *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="sd-edit__input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={agreement.tariffPrice != null ? String(Math.round(agreement.tariffPrice)) : ''}
                disabled={busy}
              />
              {amountIssue && (
                <div style={{ color: 'var(--red-fg)', fontSize: 11.5, marginTop: 4 }}>
                  {amountIssue}
                </div>
              )}
              {!amountIssue && agreement.tariffPrice != null && Number(amount) !== Math.round(agreement.tariffPrice) && (
                <div style={{ color: 'var(--fg-faint)', fontSize: 11.5, marginTop: 4 }}>
                  Тариф {fmtAmount(agreement.tariffPrice)} — сумма отличается, не забудьте пояснить в комментарии.
                </div>
              )}
            </div>

            {/* Комментарий */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Комментарий</label>
              <textarea
                className="sd-edit__input"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                placeholder="Например: «скидка по семейным обстоятельствам», «доплата за каникулы»…"
                disabled={busy}
              />
              <div style={{ color: 'var(--fg-faint)', fontSize: 11, marginTop: 2, textAlign: 'right' }}>
                {comment.length} / 500
              </div>
            </div>

          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={busy}>
              Отмена
            </button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={submitDisabled}>
              {busy ? 'Создаём…' : 'Создать начисление'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
